import { Object3D } from 'three';
import Engine from './Engine';
import Utils from './Utilities';
import Scenario from "./scenarioElements/Scenario";
import ProjectSaver from './ProjectSaver';
import DefaultSceneManager from './DefaultSceneManager';
import { assert } from './utilities/ErrorHandling';
import SceneObjectUtils from './utilities/SceneObjectUtils';
import { getUuidv4 } from './utilities/Misc';
import { CustomEventTypes, SaveModificationTypes } from '../globals';
import { IObjectGroup, ISavedObjectGroupData, IGroupsSaveData } from "../interfaces/ObjectGroup";

export class ObjectGroup implements IObjectGroup
{
	name: string;
	guid: string;
	sceneGuidToSceneObjects: Map<string, Object3D[]>;
	sceneGuidToTimelineObjects: Map<string, any[]>;

	constructor(name: string, guid?: string)
	{
		this.name = name ?? Utils.getUniqueElementName(Array.from(GroupsManager.groups.values()).map(group => { return { name: group.name } }), "New Group");
		this.guid = guid ?? getUuidv4();
		this.sceneGuidToSceneObjects = new Map<string, Object3D[]>();
		this.sceneGuidToTimelineObjects = new Map<string, any[]>();
	}
}

export class GroupsManager
{
	static instance: GroupsManager;
	static groups = new Map<string, ObjectGroup>();

	static clearData()
	{
		for (const group of this.groups.values())
		{
			group.sceneGuidToSceneObjects.clear();
			group.sceneGuidToTimelineObjects.clear();
		}
		GroupsManager.groups.clear();
		document.dispatchEvent(new CustomEvent(CustomEventTypes.onGroupListUpdated));
	}

	static getGroupByName(groupName: string)
	{
		for (const group of GroupsManager.groups.values())
		{
			if (group.name === groupName)
			{
				return group;
			}
		}
		return null;
	}

	static getGroupByGuid(groupGuid: string)
	{
		return GroupsManager.groups.get(groupGuid);
	}

	static createGroup(groupName: string, groupGuid?: string, objects?: Object3D[])
	{
		let group = GroupsManager.getGroupByName(groupName);
		if(group)
		{
			console.error("attempting to create a new group with name '" + groupName + "' but a group with that name already exists!");
		}
		else
		{
			group = new ObjectGroup(groupName, groupGuid);
			GroupsManager.groups.set(group.guid, group);
		}
		if (objects)
		{
			if (Scenario.currentStep()?.isDefault()) 
			{
				DefaultSceneManager.getInstance().syncGroupChange(objects, group.guid, true);
			}
			GroupsManager.addToGroup(group.guid, objects);
		}

		document.dispatchEvent(new CustomEvent(CustomEventTypes.onGroupListUpdated));
		return group;
	}

	static removeGroup(groupName: string)
	{
		const group = GroupsManager.getGroupByName(groupName);
		assert(group);
		if(!group) return; // this isn't strictly necessary, but it makes the compiler happy

		const hasTimelineReferences = group.sceneGuidToTimelineObjects.size > 0;
		if (hasTimelineReferences) Scenario.currentStep()?.timeline?.resetAnimations();

		for (const sceneObjectList of group.sceneGuidToSceneObjects.values())
		{
			for (const object of sceneObjectList)
			{
				const idx = object.userData.groups.indexOf(group.guid);
				assert(idx >= 0);
				object.userData.groups.splice(idx, 1);
			}
		}

		GroupsManager.groups.delete(group.guid);
		if (hasTimelineReferences) Scenario.currentStep()?.timeline?.update();
		document.dispatchEvent(new CustomEvent(CustomEventTypes.onGroupListUpdated));
	}

	static getGroupObjects(groupGuid: string, sceneGuid = "")
	{
		const group = GroupsManager.getGroupByGuid(groupGuid);
		assert(group);
		if(!group) return []; // this isn't strictly necessary, but it makes the compiler happy

		if (sceneGuid)
		{
			return group.sceneGuidToSceneObjects.get(sceneGuid) ?? [];
		}
		else
		{
			let ret = new Array<Object3D>();
			for (const sceneObjectList of group.sceneGuidToSceneObjects.values())
			{
				ret = ret.concat(sceneObjectList);
			}
			return ret;
		}
	}

	static addToGroup(groupGuid: string, objects: Object3D[], sceneGuid = "")
	{
		const group = GroupsManager.getGroupByGuid(groupGuid);
		assert(group);
		if(!group) return; // this isn't strictly necessary, but it makes the compiler happy
		if (!sceneGuid) sceneGuid = Engine.getInstance().getCurrentScene()!.userData.guid;

		for (const object of objects)
		{
			if (!object.userData.groups) object.userData.groups = [];
			if (!object.userData.groups.includes(groupGuid)) object.userData.groups.push(groupGuid);
			ProjectSaver.getInstance().markModifiedObject(object, SaveModificationTypes.Groups);
		}

		const hasTimelineReferences = group.sceneGuidToTimelineObjects.has(sceneGuid);
		if (hasTimelineReferences) Scenario.currentStep()?.timeline?.resetAnimations();

		if (group.sceneGuidToSceneObjects.has(sceneGuid))
		{
			const groupSceneObjects = group.sceneGuidToSceneObjects.get(sceneGuid) ?? [];
			for (const object of objects)
			{
				if (!groupSceneObjects.includes(object))
				{
					groupSceneObjects.push(object);
				}
			}
		}
		else
		{
			group.sceneGuidToSceneObjects.set(sceneGuid, []);
			for (const object of objects)
			{
				group.sceneGuidToSceneObjects.get(sceneGuid)?.push(object);
			}
		}

		if (hasTimelineReferences) Scenario.currentStep()?.timeline?.update();
		document.dispatchEvent(new CustomEvent(CustomEventTypes.onGroupListUpdated));
	}

	static removeFromGroup(groupGuid: string, objects: Object3D[], sceneGuid = "", onObjectDeleted = false)
	{
		const group = GroupsManager.getGroupByGuid(groupGuid);
		assert(group);
		if(!group) return; // this isn't strictly necessary, but it makes the compiler happy

		if (!sceneGuid) sceneGuid = Engine.getInstance().getCurrentScene()!.userData.guid;
		assert(group.sceneGuidToSceneObjects.has(sceneGuid));

		const hasTimelineReferences = group.sceneGuidToTimelineObjects.has(sceneGuid);
		if (hasTimelineReferences) Scenario.currentStep()?.timeline?.resetAnimations();

		const groupSceneObjects = group.sceneGuidToSceneObjects.get(sceneGuid) ?? [];
		for (const object of objects)
		{
			let idx = groupSceneObjects.indexOf(object);
			if (idx >= 0)
			{
				groupSceneObjects.splice(idx, 1);

				//we wanna maintain userData.groups for objects being deleted, but not for manual group removals
				if (onObjectDeleted) continue;
				idx = object.userData.groups.indexOf(group.guid);
				assert(idx >= 0);
				object.userData.groups.splice(idx, 1);
				ProjectSaver.getInstance().markModifiedObject(object, SaveModificationTypes.Groups);
			}
		}

		if (hasTimelineReferences) Scenario.currentStep()?.timeline?.update();

		document.dispatchEvent(new CustomEvent(CustomEventTypes.onGroupListUpdated));
	}

	static renameGroup(groupGuid: string, newName: string)
	{
		if (!newName) return;
		const group = GroupsManager.getGroupByGuid(groupGuid);		
		assert(group);
		if(!group) return; // this isn't strictly necessary, but it makes the compiler happy
		assert(group.name !== newName);
		assert(!GroupsManager.getGroupByName(newName));
		
		group.name = newName;
		document.dispatchEvent(new CustomEvent(CustomEventTypes.onGroupListUpdated));
	}

	static linkGroupToTimelineObject(groupGuid: string, sceneGuid: string, timelineObject: any)
	{
		const group = GroupsManager.getGroupByGuid(groupGuid);
		assert(group);
		if(!group) return; // this isn't strictly necessary, but it makes the compiler happy

		if (!group.sceneGuidToTimelineObjects.has(sceneGuid))
		{
			group.sceneGuidToTimelineObjects.set(sceneGuid, []);
		}

		const timelineObjects = group.sceneGuidToTimelineObjects.get(sceneGuid);
		assert(timelineObjects);
		if(!timelineObjects) return; // this isn't strictly necessary, but it makes the compiler happy
		assert(!timelineObjects.includes(timelineObject));
		
		timelineObjects.push(timelineObject);
	}

	static getGroupList()
	{
		return Array.from(GroupsManager.groups.values());
	}

	static save() : IGroupsSaveData
	{
		const output : IGroupsSaveData = { groups: new Array<ISavedObjectGroupData>(), };
		const groupList = GroupsManager.getGroupList();
		for (const group of groupList)
		{
			const groupData = { guid: group.guid, name: group.name };
			output.groups.push(groupData);
		}

		return output;
	}

	static load(savedData: { groups: ISavedObjectGroupData[] })
	{
		for (const groupData of savedData.groups)
		{
			GroupsManager.createGroup(groupData.name, groupData.guid);
		}
	}

	static load_old(data: { groups: any })
	{
		if (data && data.groups)
		{
			for (const groupData of data.groups)
			{
				GroupsManager.createGroup(groupData.name, groupData.guid);

				for (const sceneData of groupData.scenes)
				{
					const sceneRoot = Engine.getInstance().getSelectionRoot(sceneData.guid);
					if (!sceneRoot) continue;
					const objects = new Array<Object3D>();
					for (const objectGuid of sceneData.objects)
					{
						const object = SceneObjectUtils.findObjectByGuid(sceneRoot, objectGuid);
						if (object) objects.push(object);
					}
					if (objects.length) GroupsManager.addToGroup(groupData.guid, objects, sceneData.guid);
				}
			}
		}
	}
}
